import React, { useState } from 'react';
import {
  SUPPRESSED_HINT,
  SUPPRESSED_LABEL,
  formatCount,
  formatPercent,
  humanize,
  rate,
} from './format';
import { genderInsight } from './notes';

const DIMENSIONS = [
  { key: 'gender', label: 'Gender', noun: 'gender' },
  { key: 'region', label: 'Region', noun: 'region' },
  { key: 'country', label: 'Country', noun: 'country' },
  { key: 'age_band', label: 'Age', noun: 'age' },
  { key: 'education', label: 'Education', noun: 'education' },
];

const GENDER_COLOURS = {
  male: '#00999E',
  female: '#8B173B',
  non_binary: '#E08AA5',
};
const MUTED = ['Unknown', 'Other'];

const groupLabel = (value) => {
  if (value === 'male') return 'Men';
  if (value === 'female') return 'Women';
  return humanize(value);
};

function StackedShare({ buckets }) {
  const shown = buckets.filter(
    (b) => (b.enrolled ?? 0) > 0 || b.enrolled === null
  );
  return (
    <>
      <div className="flex h-7 w-full overflow-hidden rounded-md bg-gray-100">
        {shown
          .filter((b) => b.percent)
          .map((b) => (
            <div
              key={b.value}
              title={`${groupLabel(b.value)}: ${formatCount(
                b.enrolled
              )} (${formatPercent(b.percent)})`}
              style={{
                width: `${b.percent}%`,
                backgroundColor: GENDER_COLOURS[b.value] ?? '#9CA3AF',
              }}
            />
          ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 list-none p-0 m-0 text-sm">
        {shown.map((b) => (
          <li key={b.value} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: GENDER_COLOURS[b.value] ?? '#9CA3AF' }}
            />
            <span className="text-gray-700">{groupLabel(b.value)}</span>
            <span
              className="font-semibold tabular-nums text-gray-900"
              title={b.enrolled === null ? SUPPRESSED_HINT : undefined}
            >
              {formatCount(b.enrolled)}
            </span>
            <span className="text-gray-400 tabular-nums">
              · {formatPercent(b.percent)}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function BarRows({ rows, valueOf, mode, colourOf, max }) {
  return (
    <ul className="list-none p-0 m-0 space-y-2">
      {rows.map((row) => {
        const value = valueOf(row);
        return (
          <li
            key={row.value}
            className="grid grid-cols-[minmax(0,8rem)_1fr_6rem] gap-3 items-center text-sm"
          >
            <span
              className={`truncate ${
                MUTED.includes(row.value) ? 'text-gray-500' : 'text-gray-800'
              }`}
              title={groupLabel(row.value)}
            >
              {groupLabel(row.value)}
            </span>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              {value === null ? (
                <div className="h-full w-full rounded-full border border-dashed border-gray-300 bg-white" />
              ) : (
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${
                      max ? Math.max((value / max) * 100, value > 0 ? 1 : 0) : 0
                    }%`,
                    backgroundColor: colourOf(row),
                  }}
                />
              )}
            </div>
            <span className="text-right text-xs tabular-nums text-gray-700">
              {mode === 'rate' ? (
                <RateLabel bucket={row} />
              ) : (
                <ShareLabel bucket={row} />
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const rateOf = (b) => rate(b.graduated, b.enrolled);

function ShareLabel({ bucket }) {
  if (bucket.enrolled === null) {
    return <span title={SUPPRESSED_HINT}>{SUPPRESSED_LABEL}</span>;
  }
  return (
    <>
      <span className="font-semibold">{formatCount(bucket.enrolled)}</span>{' '}
      <span className="text-gray-400">({formatPercent(bucket.percent)})</span>
    </>
  );
}

function RateLabel({ bucket }) {
  const hidden = bucket.graduated === null || bucket.enrolled === null;
  return (
    <span
      className="font-semibold"
      title={
        hidden
          ? SUPPRESSED_HINT
          : `${formatCount(bucket.graduated)} of ${formatCount(
              bucket.enrolled
            )}`
      }
    >
      {formatPercent(rateOf(bucket))}
    </span>
  );
}

function LearnersCard({ title, breakdowns, scopeNote }) {
  const available = DIMENSIONS.filter(
    (d) => (breakdowns?.[d.key] ?? []).length > 0
  );
  const [selected, setSelected] = useState('gender');
  const dimension =
    available.find((d) => d.key === selected) ?? available[0] ?? DIMENSIONS[0];
  const buckets = breakdowns?.[dimension.key] ?? [];
  // Empty groups (0 learners) add nothing to either side.
  const groups = buckets.filter((b) => b.enrolled !== 0);
  const maxShare = Math.max(...groups.map((b) => b.percent ?? 0), 1);
  const maxRate = Math.max(...groups.map((b) => rateOf(b) ?? 0), 1);
  const colourOf = (b) =>
    MUTED.includes(b.value) ? '#9CA3AF' : GENDER_COLOURS[b.value] ?? '#00999E';
  const insight = dimension.key === 'gender' ? genderInsight(buckets) : null;

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {available.length > 1 && (
          <div className="flex flex-wrap gap-1" role="tablist">
            {available.map((d) => (
              <button
                key={d.key}
                type="button"
                role="tab"
                aria-selected={d.key === dimension.key}
                onClick={() => setSelected(d.key)}
                className={`px-3 py-1 rounded-md text-xs border transition-colors ${
                  d.key === dimension.key
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'border-gray-200 text-gray-600 hover:text-gray-900'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-gray-500">No data for this breakdown.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-medium text-gray-700 mb-3">
              Share of learners
            </p>
            {dimension.key === 'gender' ? (
              <StackedShare buckets={buckets} />
            ) : (
              <BarRows
                rows={groups}
                max={maxShare}
                valueOf={(b) => (b.enrolled === null ? null : b.percent ?? 0)}
                colourOf={colourOf}
                mode="share"
              />
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700 mb-3">
              Graduation rate by {dimension.noun}
            </p>
            <BarRows
              rows={groups}
              max={maxRate}
              valueOf={rateOf}
              colourOf={colourOf}
              mode="rate"
            />
            <p className="mt-3 text-xs text-gray-500">
              Graduates ÷ all enrolled in {scopeNote}, including live cohorts,
              so these read lower than the headline rate.
              {insight ? ` ${insight}` : ''}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

export default LearnersCard;
